import { parseDate, parseAbsolute } from '@internationalized/date';
import { DateValue } from 'react-aria-components';
import { type CalendarProps } from '../../components/_base/Calendar';

/**
 * Function to convert a string into ReactAria's DateValue type
 */
export function parseDateValue(
  value?: DateValue | string,
): DateValue | undefined {
  if (!value) return undefined;

  if (typeof value === 'string') {
    try {
      // First try parsing as absolute time
      return parseAbsolute(value, 'UTC');
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      try {
        // Fall back to parsing as local datetime
        return parseDate(value);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (err) {
        // Don't log error for incomplete dates
        return undefined;
      }
    }
  }
  return value;
}

/**
 * Function to get the visible range and prevent 'too many rerenders' errors
 */
const defaultVisibleDuration = { months: 1 };

export function getVisibleDuration(
  visibleDuration: CalendarProps['visibleDuration'] = defaultVisibleDuration,
) {
  if (
    !visibleDuration?.days &&
    !visibleDuration?.weeks &&
    !visibleDuration?.months &&
    !visibleDuration?.years
  ) {
    return defaultVisibleDuration;
  }
  // Convert the 'years' property to 'months'
  if (visibleDuration?.years) {
    return { months: visibleDuration.years * 12 };
  }
  return visibleDuration;
}

/**
 * Function to get week range for a given date (used in stories)
 */
export const getWeekRange = (date: Date) => {
  const mondaysDate =
    date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1);
  const sundaysDate = mondaysDate + 6;

  return {
    start: new Date(
      date.getFullYear(),
      date.getMonth(),
      mondaysDate,
    ).toISOString(),
    end: new Date(
      date.getFullYear(),
      date.getMonth(),
      sundaysDate,
    ).toISOString(),
  };
};
